/**
 * /orders/family page data layer (Plan 07).
 *
 * The family page reads the parent's household (students + the
 * locations they're enrolled at) via the shared `MyFamily` shape — see
 * `api/family.ts`. The page-specific `listOrderingStudents` projection
 * lives here so the helper sits next to its consumer.
 *
 * Cart + week-lunchbox queries land when the GraphQL surface they
 * depended on ships its REST equivalents; today the page uses the
 * household read + client-side cart state until then.
 */

import type { UserAvatarColor } from '@/lib/user-avatar-colors'

import type { MyFamilyResponse } from './family'
import { apiFetch } from './http'
import type { Mealtime } from './school-orders'

// Re-export the household read hook + types from the shared family
// module so call sites can keep importing from `orders-family`.
export {
  fetchMyFamily as fetchMyOrderingFamily,
  type MyFamilyResponse as MyOrderingFamilyResponse,
  useMyFamilyQuery as useMyOrderingFamilyQuery,
} from './family'

// ---------------------------------------------------------------------------
// Place a new FAMILY_DIRECT order for one (serviceDate, mealtime). Hits the
// dedicated family submit endpoint (household-billed via CartManager) — NOT
// the school cart pipeline, which is organization-billed + SCHOOL_FORECAST.
// ---------------------------------------------------------------------------

export interface SubmitFamilyOrderGroupInput {
  accountId: string
  organizationId: string
  serviceLocationId: string
  /** ISO instant for the service day. */
  serviceDate: string
  mealtime: Mealtime
  /**
   * `expectedUnitPriceCents` is the per-unit price the parent was
   * shown. The server's reprice guard compares it against the fresh
   * service-date resolve and answers 409 `price_changed` on drift.
   */
  items: Array<{
    menuItemId: string
    studentUserId: string
    quantity: number
    expectedUnitPriceCents?: number
    /** Set for doctor's-note special-diet lines so the server enforces approval + daily cap. */
    specialDietAccommodationId?: string | null
  }>
  /**
   * True only after the parent confirmed the new prices in the
   * PriceChangedDialog — standing "submit at the current price".
   */
  acceptRepricing?: boolean
  /**
   * One key per checkout attempt (FACTORY-542). A request the network or the
   * mesh replays carries the same body, so the server returns the order the
   * first copy placed instead of placing — and charging — a second.
   */
  idempotencyKey?: string
}

export interface SubmitFamilyOrderGroupResponse {
  id: string
  status: string
  totalCents: number
}

export async function submitFamilyOrderGroup(
  input: SubmitFamilyOrderGroupInput,
): Promise<SubmitFamilyOrderGroupResponse> {
  return apiFetch<SubmitFamilyOrderGroupResponse>('/api/family-orders/submit', {
    method: 'POST',
    body: { ...input, idempotencyKey: input.idempotencyKey ?? crypto.randomUUID() },
  })
}

export interface OrderingStudent {
  /** AccountMember.id — the row that scopes cart lines. */
  accountMemberId: string
  /** User.id — the student's underlying identity. */
  userId: string
  /**
   * HOUSEHOLD Account the student belongs to — the account their order
   * lines must be billed to. A guardian can span multiple households
   * (own kids + a claimed school-provisioned household), so this is
   * per-student, not per-viewer.
   */
  accountId: string
  /**
   * True when this diner is the household owner ordering for themselves —
   * a teacher / staff self-diner. Surfaced as "You" and sorted first.
   */
  isSelf: boolean
  displayName: string
  initials: string
  avatarUri: string | null
  avatarColor: UserAvatarColor
  /**
   * The student's profile allergen tags (canonical contains-policy Tag ids —
   * the same ids the menus payload stamps on item allergens, so ordering
   * surfaces intersect by id with no vocabulary mapping). Drives the
   * non-blocking dietary-conflict warning in the lunchbox drawer.
   */
  allergenTags: ReadonlyArray<{ id: string; name: string }>
  /**
   * Schools the student is enrolled at (deduped + active only).
   * `organizationId` was added with Plan 47 so the family page can
   * look up the school's ordering-channel config without a second
   * round-trip (the membership carries org + location together).
   */
  locations: Array<{
    id: string
    name: string
    timeZone: string
    organizationId: string
    gradeLevel: string | null
    gradeRangeId: string | null
    gradeRangeCode: string | null
    /**
     * True when the member holds an active STUDENT membership at this campus
     * (or an org-wide STUDENT membership not yet narrowed to a campus).
     * Students are grade-band gated on the ordering surfaces — fail-closed
     * when `gradeRangeCode` is null. False = a staff self-diner (TEACHER
     * membership): band-less BY DESIGN, sees every menu.
     */
    isStudent: boolean
    /**
     * Order-deadline policy. FIXED_WEEKLY uses dayOfWeek/weeksBefore (null
     * day-of-week ⇒ no deadline); RELATIVE_DAYS uses relativeDays.
     */
    orderDeadlineMode: 'FIXED_WEEKLY' | 'RELATIVE_DAYS'
    orderDeadlineDayOfWeek: string | null
    orderDeadlineWeeksBefore: number
    orderDeadlineTime: string
    orderDeadlineRelativeDays: number | null
    /**
     * False = the campus takes no parent orders (school ordering only,
     * PROD-1554): the family surfaces show its menu view-only. Absent reads
     * as true — test the flag through `acceptsParentOrders`.
     */
    parentOrderingEnabled?: boolean
  }>
}

/**
 * Collapse EVERY accessible household's members into the list of
 * students the parent can order for — a guardian can span multiple
 * households (their own kids plus a claimed school-provisioned
 * household). Filters archived rows + non-student memberships, then
 * derives initials + display name in a way that matches Plan 06's
 * `getDisplayName`. A student appearing in two households keeps their
 * first (oldest-household) row.
 */
export function listOrderingStudents(data: MyFamilyResponse | null | undefined): OrderingStudent[] {
  const households = (data?.me?.ownedAccounts ?? []).filter((a) => a.kind === 'HOUSEHOLD' && !a.isArchived)
  if (households.length === 0) {
    return []
  }
  const meId = data?.me?.id ?? null
  const seenUserIds = new Set<string>()
  const out: OrderingStudent[] = []
  const members = households.flatMap((household) => household.members.map((member) => ({ household, member })))
  for (const { household, member } of members) {
    if (member.isArchived || !member.user || seenUserIds.has(member.user.id)) {
      continue
    }
    seenUserIds.add(member.user.id)
    const u = member.user
    const isSelf = meId !== null && u.id === meId
    const realDisplayName = u.preferredName && u.preferredName !== u.firstName ? u.preferredName : u.firstName
    // The household owner ordering for themselves reads as "You"; the avatar
    // initials still use their real name so the chip stays recognizable.
    const displayName = isSelf ? 'You' : realDisplayName
    const initials = initialsOf(realDisplayName, u.lastName)
    const enrollmentByLocationId = new Map(u.enrollments.map((enrollment) => [enrollment.locationId, enrollment]))
    // Band-gating discriminator (mirrors the server's rule in
    // managers/student-band.ts): a STUDENT membership at the campus — or an
    // org-wide STUDENT membership with no campus narrowing yet — marks the
    // member a student there; anything else (TEACHER self-diner) is
    // band-less by design.
    const isStudentMembership = (ms: (typeof u.memberships)[number]): boolean =>
      !ms.isArchived && ms.profileCode === 'STUDENT'
    const hasUnscopedStudentMembership = u.memberships.some((ms) => isStudentMembership(ms) && !ms.location)
    const studentLocationIds = new Set(
      u.memberships.filter((ms) => isStudentMembership(ms) && ms.location).map((ms) => ms.location!.id),
    )
    const seenLocations = new Map<string, OrderingStudent['locations'][number]>()
    for (const ms of u.memberships) {
      if (ms.isArchived || !ms.location || ms.location.type !== 'SCHOOL_CAMPUS') {
        continue
      }
      const enrollment = enrollmentByLocationId.get(ms.location.id)
      seenLocations.set(ms.location.id, {
        id: ms.location.id,
        name: ms.location.name,
        timeZone: ms.location.timeZone,
        organizationId: ms.organizationId,
        gradeLevel: enrollment?.gradeLevel ?? null,
        gradeRangeId: enrollment?.gradeRange?.id ?? null,
        // Explicit school grade→band match ONLY (2026-07 fail-closed flip):
        // no name-matcher guessing — an unmatched grade is null and the menu
        // gate blocks it.
        gradeRangeCode: enrollment?.gradeRange?.code ?? null,
        isStudent: hasUnscopedStudentMembership || studentLocationIds.has(ms.location.id),
        orderDeadlineMode: ms.location.orderDeadlineMode ?? 'FIXED_WEEKLY',
        orderDeadlineDayOfWeek: ms.location.orderDeadlineDayOfWeek ?? null,
        orderDeadlineWeeksBefore: ms.location.orderDeadlineWeeksBefore ?? 0,
        orderDeadlineTime: ms.location.orderDeadlineTime ?? '23:59',
        orderDeadlineRelativeDays: ms.location.orderDeadlineRelativeDays ?? null,
        parentOrderingEnabled: ms.location.parentOrderingEnabled ?? true,
      })
    }
    out.push({
      accountMemberId: member.id,
      userId: u.id,
      accountId: household.id,
      isSelf,
      displayName,
      initials,
      avatarUri: u.avatarUri,
      avatarColor: u.avatarColor,
      allergenTags: (u.allergenTags ?? []).map((tag) => ({ id: tag.id, name: tag.name })),
      locations: Array.from(seenLocations.values()).sort((a, b) => a.name.localeCompare(b.name)),
    })
  }
  // Self ("You") sorts first, then everyone else alphabetically.
  return out.sort((a, b) => {
    if (a.isSelf !== b.isSelf) {
      return a.isSelf ? -1 : 1
    }
    return a.displayName.localeCompare(b.displayName)
  })
}

/**
 * Should the signed-in staff member be provisioned as their own diner?
 *
 * Keyed on whether they are ALREADY in the diner list — NOT on the list being
 * empty. A teacher who is also a parent has children here from day one, so an
 * emptiness check never fires for them and they could never order for
 * themselves (PROD-1163). `POST /api/family/self` is idempotent and
 * server-gated on an active TEACHER membership, so the only job left on the
 * client is to stop asking once the self-diner is present.
 */
export function needsSelfDinerProvisioning(
  students: ReadonlyArray<Pick<OrderingStudent, 'isSelf'>>,
  isStaff: boolean,
): boolean {
  return isStaff && !students.some((student) => student.isSelf)
}

function initialsOf(first: string | null | undefined, last: string | null | undefined): string {
  const f = (first ?? '').trim().charAt(0).toUpperCase()
  const l = (last ?? '').trim().charAt(0).toUpperCase()
  if (!f && !l) {
    return '?'
  }
  return `${f}${l}`
}
